import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, IconButton,
  Box, Typography, TextField, Button, CircularProgress, Alert,
} from '@mui/material';
import { Edit, Close } from '@mui/icons-material';
import { adminApi } from '../services/hrmsApi';

export default function EditUserProfileDialog({ open, user, onClose, onSaved }) {
  const [form,   setForm]   = useState({ fullName: '', email: '', department: '' });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState(null);

  useEffect(() => {
    if (user) {
      setForm({ fullName: user.fullName || '', email: user.email || '', department: user.department || '' });
      setErrors({});
      setError(null);
    }
  }, [user]);

  const setField = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleClose = () => { if (!saving) onClose(); };

  const validate = () => {
    const errs = {};
    if (!form.fullName.trim()) errs.fullName = 'Required';
    if (!form.email.trim())    errs.email    = 'Required';
    return errs;
  };

  const submit = async () => {
    const errs = validate();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }

    setSaving(true);
    setError(null);
    try {
      const updated = await adminApi.updateUserProfile(user.id, form);
      onSaved(updated);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update profile.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Edit color="primary" />
          <Typography variant="h6" fontWeight={700}>Edit Profile</Typography>
        </Box>
        <IconButton onClick={handleClose} disabled={saving} size="small">
          <Close fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField label="Username" size="small" fullWidth value={user?.username || ''}
            disabled InputProps={{ readOnly: true }} helperText="Username cannot be changed" />
          <TextField
            label="Full Name *" size="small" fullWidth
            value={form.fullName} onChange={setField('fullName')}
            error={!!errors.fullName} helperText={errors.fullName}
          />
          <TextField
            label="Email *" type="email" size="small" fullWidth
            value={form.email} onChange={setField('email')}
            error={!!errors.email} helperText={errors.email}
          />
          <TextField
            label="Department" size="small" fullWidth
            value={form.department} onChange={setField('department')}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button onClick={handleClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={submit} disabled={saving}
          startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <Edit />}>
          {saving ? 'Saving…' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
